import { IconEye, IconPen, SmallButton } from 'components';
import dayjs from 'dayjs';
import { formatPriceWithComma, openUrlWithRole } from 'utils/helpers';
import { DATE_FORMAT, INVOICE_STATUS, ROLE_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';
import { getRequestData, submitInvoiceByManager } from 'api/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { endpoints } from 'api/endpoints';
import { useAuth } from 'context/authContext';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

export const SingleInvoiceRow = ({ invoice, onEditClick, isInEditMode, onSubmit }) => {
  const { role } = useAuth();
  const [invoiceData, setInvoiceData] = useState(invoice);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  useEffect(() => {
    setInvoiceData(invoice);
  }, [invoice]);

  const clickHandler = useCallback(
    (invoice_id) => {
      setLoadingInvoice(true);
      submitInvoiceByManager(invoice_id, () => {
        getRequestData(endpoints.getInvoice(invoice_id), ROLE_TYPES.slh_manager, (data) => {
          setInvoiceData(data);
          setLoadingInvoice(false);
          onSubmit?.();
        });
      });
    },
    [onSubmit],
  );
  const onEyeClick = useCallback(() => {
    setLoadingInvoice(true);
    getRequestData(
      endpoints.exportInvoice(invoiceData.invoice_id),
      ROLE_TYPES.slh_manager,
      (data) => {
        openUrlWithRole(data.url, role || ROLE_TYPES.slh_manager);
        setLoadingInvoice(false);
      },
    );
  }, [invoiceData.invoice_id]);

  const storeButton = useMemo(() => {
    if (role === ROLE_TYPES.admin) {
      return (
        <a
          href={invoiceData.url}
          onClick={(e) => {
            e.preventDefault();
            openUrlWithRole(invoiceData.url, role);
          }}
          rel="noreferrer"
        >
          <SmallButton
            btnClassName="h-10"
            disabled={invoiceData.status === INVOICE_STATUS.pending}
            label="Download"
          />
        </a>
      );
    }

    if (role === ROLE_TYPES.slh_manager && invoiceData.status === INVOICE_STATUS.ready) {
      return (
        <a
          href={invoiceData.url}
          onClick={(e) => {
            e.preventDefault();
            openUrlWithRole(invoiceData.url, role);
          }}
          rel="noreferrer"
        >
          <SmallButton btnClassName="h-10" label="Download" />
        </a>
      );
    }

    return (
      <div className="flex items-center justify-end gap-x-2">
        {loadingInvoice && (
          <Spin indicator={<LoadingOutlined spin style={{ color: '#FDB513' }} />} size="large" />
        )}
        {isInEditMode && (
          <IconEye
            onClick={onEyeClick}
            color={COLORS.blue}
            className="bg-white p-1.5 w-8 h-8 shadow-block-shadow rounded-lg cursor-pointer"
          />
        )}
        {!isInEditMode && (
          <IconPen
            onClick={onEditClick}
            color={COLORS.blue}
            className="bg-white p-1.5 w-8 h-8 shadow-block-shadow rounded-lg cursor-pointer"
          />
        )}
        {invoiceData.status === INVOICE_STATUS.pending && role === ROLE_TYPES.slh_manager && (
          <SmallButton
            onClick={() => clickHandler(invoiceData.invoice_id)}
            btnClassName="!bg-yellow !text-orange !h-10"
            label="Submit"
          />
        )}
      </div>
    );
  }, [role, invoiceData, loadingInvoice, isInEditMode, onEyeClick, onEditClick, clickHandler]);

  return (
    <div className="flex items-center w-full border-b border-color-divider py-4">
      <p className="w-[25%] text-14 font-medium">{`${dayjs(invoiceData.week_start).format(DATE_FORMAT)} - ${dayjs(invoiceData.week_end).format(DATE_FORMAT)}`}</p>
      <p className="w-[22%] text-14 font-medium">{invoiceData.partner.name}</p>
      <p className="w-[11%] text-14 font-medium">{invoiceData.active_clients}</p>
      <p className="w-[14%] text-14 font-medium">${formatPriceWithComma(invoiceData.total)}</p>
      <p
        className={`w-[11%] text-14 font-medium ${invoiceData.status === INVOICE_STATUS.pending ? 'text-orange' : invoiceData.status === INVOICE_STATUS.paid ? 'text-green' : 'text-black'}`}
      >
        {INVOICE_STATUS[invoiceData.status.toLowerCase()]}
      </p>
      <div className="flex items-center justify-end w-[17%]">{storeButton}</div>
    </div>
  );
};
